import * as THREE from "three";
import { GIT_BRANCH } from "../generated/git_branch.js";
import {
  DebugSettings,
  GlobalSettings,
  RuntimeEnvironmentKey,
  getNextRuntimeEnvironmentKey,
  getRuntimeEnvironmentLabel,
  getServerRoutingOverride,
  setRuntimeEnvironmentOverride,
  setServerRoutingOverride,
} from "../global_settings.js";
import { ScreenManager, Screens } from "../screen_manager/screen_manager.js";
import { ServerRoutingService } from "../server_routing/server_routing_service.js";
import { RewardWidgetsService } from "../reward_widgets/reward_widgets_service.js";
import { HappyFallScreen } from "../ui/screens/happy_fall_screen/happy_fall_screen.js";
import { StressTest } from "./stress_test.js";
import { TelegramAPISettings } from "../telegram_api_settings.js";
import { BlockShowcase } from "./block_showcase.js";
import { DiamondTowerShowcase } from "./diamond_tower_showcase.js";
import { JarPyramidShowcase } from "./jar_pyramid_showcase.js";
import { TiltedTableShowcase } from "./tilted_table_showcase.js";
import { RotatedTableShowcase } from "./rotated_table_showcase.js";
import { ShelfTableShowcase } from "./shelf_table_showcase.js";
import { HangingTableShowcase } from "./hanging_table_showcase.js";
import { ElevatorTableShowcase } from "./elevator_table_showcase.js";
import { SlideTableShowcase } from "./slide_table_showcase.js";
import { TableCaseShowcase } from "./table_case_showcase.js";
import { LeaningLogsShowcase } from "./leaning_logs_showcase.js";
import { JarCasesShowcase } from "./jar_cases_showcase.js";
import { JarSandwichShowcase } from "./jar_sandwich_showcase.js";
import { JarBayShowcase } from "./jar_bay_showcase.js";
import { TextureTest } from "./texture_test.js";
import { TableFormationShowcase } from "./table_formation_showcase.js";
import { NewBlockModelsShowcase } from "./new_block_models_showcase.js";
import { BarrierShowcase } from "./barrier_showcase.js";
import { LevelPoolBrowser } from "./level_pool_browser.js";
import { TriangleBench } from "./triangle_bench.js";
import { RimLightTest } from "./rim_light_test.js";
import { FpsOverlay } from "./fps_overlay.js";
import { VfxBlockShatterTask } from "../vfx_3d/effects/block_shatter/vfx_block_shatter_task.js";
import { GameScreen } from "../ui/screens/game_screen/game_screen.js";
import { StatisticsStorage } from "../storage/statistics_storage.js";
import { Game } from "../game.js";
import { MemoryOverlay } from "./memory_overlay.js";
import { LogOverlay } from "./log_overlay.js";
import { MenuScreen } from "../ui/screens/menu_screen/menu_screen.js";
import { BoosterHighlightRenderer } from "../ui/overlay/booster_highlight_renderer.js";
import { OverlayFactory } from "../ui/overlay/overlay_factory.js";
import { OverlayId } from "../ui/overlay/overlay_ids.js";
import { ImagesHolder } from "../image_loader/images_holder.js";
import { ImgID } from "../image_loader/img_id.js";
import { ConveyorTutorial } from "../conveyor/conveyor_tutorial.js";
import type { FirstStepsTaskDto } from "../api/dto/reward_widgets.dto.js";
import { FIRST_STEPS_PREVIEW_DONE_STATE, FIRST_STEPS_PREVIEW_FRESH_IDS, FIRST_STEPS_PREVIEW_FRESH_STATE, FIRST_STEPS_PREVIEW_STATE } from "../ui/overlay/overlays/first_steps_overlay.js";
import { DialogManager } from "../ui/dialog/dialog_manager.js";
import { UpdateRequiredDialog, UpdateRequiredMode } from "../ui/dialog/update_required_dialog.js";
import * as MaxAds from "../ads/applovin_max.js";
import { NoLevelsDialog } from "../ui/dialog/no_levels_dialog.js";
import { PlayDialog, PlayDialogDifficulty, PlayDialogState } from "../ui/dialog/play_dialog.js";
import { NetworkManager } from "../network_manager.js";
import { BuyBoosterDialog, BuyBoosterType } from "../ui/dialog/buy_booster_dialog.js";
import { LocalizationManager } from "../localization/localization_manager.js";
import { resetDeviceIdForNewUser } from "../utils/device_id.js";
import { Vfx2DRuntime } from "../webgl_2d/vfx_2d_runtime.js";
import { AvatarSelectionDialog } from "../ui/dialog/avatar_selection_dialog.js";
import { SettingsDialog } from "../ui/dialog/settings_dialog.js";
import { ProfileDialog } from "../ui/dialog/profile_dialog.js";
import { debugSetLevel, resetProgress, rewardHeart } from "../api/requests/economy.requests.js";
import { getMaxCampaignLevel } from "../level_config.js";
import { adoptEconomyFromPlayer } from "../api/player_sync.js";
import { PhysicsDebugRenderer } from "./physics_debug_renderer.js";
import { BallTuner } from "./ball_tuner.js";
import { BlockMassTuner } from "./block_mass_tuner.js";
import { Renderer } from "../core/renderer.js";
import { MeshBatcher } from "../core/mesh_batcher.js";
import { ShadowBatcher } from "../core/shadow_batcher.js";
import { SceneManager } from "../core/scene_manager.js";
import { PixelFeatures, applyPixelFeatures } from "./pixel_features.js";
import { PhysicsWorld } from "../physics/physics_world.js";
import { CannonBall } from "../entities/cannon_ball.js";
import { Vfx3DRuntime } from "../vfx_3d/vfx_3d_runtime.js";
import { Vfx3DManager } from "../vfx_3d/vfx_3d_manager.js";
import { VfxPhysicsParticleBatch } from "../vfx_3d/core/batches/vfx_physics_particle_batch.js";
import { CumulativeBoosterID } from "../cumulative_boosters/cumulative_booster_id.js";
import { CumulativeBoosterService } from "../cumulative_boosters/cumulative_booster_service.js";
import { NotificationManager } from "../notifications/notification_manager.js";
import { OfferService } from "../offers/offer_service.js";

let entry_attached = false;
let tap_code_attached = false;
let tap_code_index = 0;
let tap_code_start_ts = 0;

const diag_render_scales = Array.from( new Set( [ window.devicePixelRatio || 1, 2, 1 ] ) );
let diag_scale_index = 0;
let diag_solver_iters = 8;

function activeGameScreen(): GameScreen | null
{
  const screen = ScreenManager.getCurrentScreenInstance();
  return screen instanceof GameScreen ? screen : null;
}

function grantBoosterUse( id: CumulativeBoosterID ): void
{
  const screen = activeGameScreen();
  if ( !screen )
  {
    console.warn( "[DebugConsole] Not currently in a game — open a level first" );
    return;
  }

  screen.addBoosterUses( id, 1 );
  updateInfoText();
}

function goToLevelPrompt(): void
{
  const current = StatisticsStorage.load().level;
  const input = window.prompt( "Go to level:", String( current ) );
  if ( input === null )
    return;

  const level = Math.trunc( Number( input ) );
  if ( !Number.isFinite( level ) || level < 1 )
  {
    window.alert( "Enter a valid level number" );
    return;
  }

  DebugConsoleManager.close();
  Game.instance.hideLoadingGameScreen();
  void ScreenManager.showScreen( Screens.GAME, { level } );
}

async function openDebugBuyBoosterDialog(
  booster_type: BuyBoosterType,
  booster_id: CumulativeBoosterID
): Promise<void>
{
  DebugConsoleManager.close();
  const offer = await CumulativeBoosterService.getOrLoadFirstPriceOffer( booster_id );
  if ( offer === null )
  {
    console.warn( `[Boosters] No price offer for booster ${booster_id}` );
    return;
  }

  DialogManager.openDialog( new BuyBoosterDialog( {
    booster_type,
    booster_id,
    offer,
    source: "debug",
  } ) );
}

function diagToggleShadows(): void
{
  const renderer = Renderer.renderer;
  renderer.shadowMap.enabled = !renderer.shadowMap.enabled;
  renderer.shadowMap.needsUpdate = true;
  SceneManager.world.traverse( obj =>
  {
    const material = ( obj as { material?: unknown } ).material;
    if ( !material ) return;
    const materials = Array.isArray( material ) ? material : [ material ];
    for ( const m of materials )
      ( m as { needsUpdate?: boolean } ).needsUpdate = true;
  } );
}

function diagCycleRenderScale(): void
{
  diag_scale_index = ( diag_scale_index + 1 ) % diag_render_scales.length;
  Renderer.setDiagnosticPixelRatio( diag_render_scales[diag_scale_index] );
}

function diagToggleSolverIters(): void
{
  diag_solver_iters = diag_solver_iters === 8 ? 0 : 8;
  PhysicsWorld.getWorld().forEachRigidBody( body => body.setAdditionalSolverIterations( diag_solver_iters ) );
}

function diagRefreshMaterials(): void
{
  SceneManager.world.traverse( obj =>
  {
    const material = ( obj as { material?: unknown } ).material;
    if ( !material ) return;
    const materials = Array.isArray( material ) ? material : [ material ];
    for ( const m of materials )
      ( m as { needsUpdate?: boolean } ).needsUpdate = true;
  } );
}

const SHADOW_PRESETS = [
  { label: "PCF 2048", type: THREE.PCFShadowMap, size: 2048 },
  { label: "PCF 1024", type: THREE.PCFShadowMap, size: 1024 },
  { label: "BASIC 1024", type: THREE.BasicShadowMap, size: 1024 },
];
let diag_shadow_index = 0;

function diagCycleShadowQuality(): void
{
  diag_shadow_index = ( diag_shadow_index + 1 ) % SHADOW_PRESETS.length;
  const preset = SHADOW_PRESETS[diag_shadow_index];

  Renderer.renderer.shadowMap.type = preset.type;

  SceneManager.world.traverse( obj =>
  {
    const light = obj as THREE.DirectionalLight;
    if ( !( light as { isDirectionalLight?: boolean } ).isDirectionalLight || !light.castShadow ) return;
    light.shadow.mapSize.set( preset.size, preset.size );
    light.shadow.map?.dispose();
    light.shadow.map = null as any;
  } );

  diagRefreshMaterials();
  Renderer.renderer.shadowMap.needsUpdate = true;
}

let diag_receive_shadows = true;

function diagToggleReceiveShadows(): void
{
  diag_receive_shadows = !diag_receive_shadows;
  SceneManager.world.traverse( obj =>
  {
    const mesh = obj as THREE.Mesh;
    if ( !( mesh as { isMesh?: boolean } ).isMesh ) return;
    const material = mesh.material;
    const first = Array.isArray( material ) ? material[0] : material;
    // The ground plane is the ShadowMaterial catcher — it must keep receiving,
    // otherwise the whole table shadow disappears and the test means nothing.
    if ( first && ( first as { isShadowMaterial?: boolean } ).isShadowMaterial ) return;
    mesh.receiveShadow = diag_receive_shadows;
  } );
  diagRefreshMaterials();
  Renderer.renderer.shadowMap.needsUpdate = true;
}

let diag_ccd = true;

function diagToggleCcd(): void
{
  diag_ccd = !diag_ccd;
  PhysicsWorld.getWorld().forEachRigidBody( body => body.enableCcd( diag_ccd ) );
}

// Each directional light costs a full BRDF evaluation per pixel on every PBR
// surface — and twice that where clearcoat is enabled. Toggling them one by one
// separates "cost of light" from "cost of material".
function diagToggleClearcoat(): void
{
  PixelFeatures.clearcoat = !PixelFeatures.clearcoat;
  applyPixelFeatures();
}

function diagToggleNormalMaps(): void
{
  PixelFeatures.normal_maps = !PixelFeatures.normal_maps;
  applyPixelFeatures();
  console.log( `[BLOCK-MODEL-DEBUG] normal maps ${PixelFeatures.normal_maps ? "ON" : "OFF"}` );
}

function diagToggleEmissiveMaps(): void
{
  PixelFeatures.emissive_maps = !PixelFeatures.emissive_maps;
  applyPixelFeatures();
}

const LIGHT_MODES = [ "ALL", "SUN+AMB", "AMB ONLY" ];
let diag_light_mode = 0;

function diagToggleFrontLight(): void
{
  GlobalSettings.DEBUG_NO_FRONT_LIGHT = !GlobalSettings.DEBUG_NO_FRONT_LIGHT;

  const no_sun = GlobalSettings.DEBUG_NO_FRONT_LIGHT;

  SceneManager.world.traverse( obj =>
  {
    const light = obj as THREE.Light & { isDirectionalLight?: boolean; isAmbientLight?: boolean; isHemisphereLight?: boolean };

    if ( light.isDirectionalLight )
      light.visible = !no_sun;
    else if ( light.isAmbientLight )
      light.intensity = no_sun ? 1.05 : 0.55;
    else if ( light.isHemisphereLight )
      light.intensity = no_sun ? 1.7 : 0.95;
  } );

  Renderer.renderer.shadowMap.needsUpdate = true;
}

function diagCycleLights(): void
{
  diag_light_mode = ( diag_light_mode + 1 ) % LIGHT_MODES.length;

  const directionals: THREE.DirectionalLight[] = [];
  SceneManager.world.traverse( obj =>
  {
    const light = obj as THREE.DirectionalLight;
    if ( ( light as { isDirectionalLight?: boolean } ).isDirectionalLight )
      directionals.push( light );
  } );

  // The shadow caster is the brightest one; keep it as the "sun".
  directionals.sort( ( a, b ) => Number( b.castShadow ) - Number( a.castShadow ) || b.intensity - a.intensity );

  directionals.forEach( ( light, index ) =>
  {
    if ( diag_light_mode === 0 ) light.visible = true;
    else if ( diag_light_mode === 1 ) light.visible = index === 0;
    else light.visible = false;
  } );

  diagRefreshMaterials();
}

// PBR keeps its cost per pixel regardless of light count: normal maps,
// clearcoat and envMap each add texture fetches and BRDF work. These variants
// strip them one tier at a time so the quality/FPS trade-off is visible.
const MATERIAL_MODES = [ "PBR", "SMART", "LITE", "PHONG", "BASIC" ] as const;
let diag_material_mode = 0;

const diag_saved_materials = new Map<THREE.Mesh, THREE.Material | THREE.Material[]>();
const diag_variant_cache = new Map<string, THREE.Material>();

function materialVariant( source: THREE.Material, mode: number ): THREE.Material
{
  const key = `${source.uuid}|${mode}`;
  const cached = diag_variant_cache.get( key );
  if ( cached ) return cached;

  const src = source as THREE.MeshStandardMaterial;
  const color = src.color ? src.color.clone() : new THREE.Color( 0xffffff );
  const common = {
    map: src.map ?? null,
    transparent: src.transparent,
    opacity: src.opacity,
    side: src.side,
    alphaTest: src.alphaTest,
  };

  let variant: THREE.Material;

  // SMART: big static surfaces (table) drop to Phong since their lighting
  // barely changes, while gameplay blocks keep PBR shading minus the extras.
  const effective_mode = mode === 1
    ? ( source.userData?.perf_tier === "large_static" ? 3 : 2 )
    : mode;

  if ( effective_mode === 2 )
  {
    // Same lighting model, minus the per-pixel extras.
    variant = new THREE.MeshStandardMaterial( {
      ...common,
      color,
      roughness: src.roughness ?? 0.5,
      metalness: src.metalness ?? 0,
    } );
  }
  else if ( effective_mode === 3 )
  {
    variant = new THREE.MeshPhongMaterial( {
      ...common,
      color,
      shininess: THREE.MathUtils.clamp( ( 1 - ( src.roughness ?? 0.5 ) ) * 90, 4, 90 ),
      specular: new THREE.Color( 0x2a2a2a ),
    } );
  }
  else
  {
    variant = new THREE.MeshBasicMaterial( { ...common, color } );
  }

  diag_variant_cache.set( key, variant );
  return variant;
}

function diagCycleMaterials(): void
{
  diag_material_mode = ( diag_material_mode + 1 ) % MATERIAL_MODES.length;

  SceneManager.world.traverse( obj =>
  {
    const mesh = obj as THREE.Mesh;
    if ( !( mesh as { isMesh?: boolean } ).isMesh || !mesh.material ) return;

    if ( !diag_saved_materials.has( mesh ) )
    {
      const current = Array.isArray( mesh.material ) ? mesh.material[0] : mesh.material;
      if ( ( current as { isMeshStandardMaterial?: boolean } ).isMeshStandardMaterial !== true ) return;
      diag_saved_materials.set( mesh, mesh.material );
    }

    const original = diag_saved_materials.get( mesh )!;

    if ( diag_material_mode === 0 )
    {
      mesh.material = original;
      return;
    }

    mesh.material = Array.isArray( original )
      ? original.map( m => materialVariant( m, diag_material_mode ) )
      : materialVariant( original, diag_material_mode );
  } );
}

let panel_el: HTMLDivElement | null = null;
let info_el: HTMLPreElement | null = null;
let dynamic_buttons: { btn: HTMLButtonElement; cfg: ButtonConfig }[] = [];
let active_debug_page = 0;
let add_heart_inflight = false;
let set_level_inflight = false;
let add_happy_fall_ball_inflight = false;
let reset_progress_inflight = false;
let notifications_debug_text = "";

const getTapQuadrant = ( x: number, y: number ): number =>
{
  const w = window.innerWidth || document.documentElement.clientWidth || 1;
  const h = window.innerHeight || document.documentElement.clientHeight || 1;
  const is_top = y < h / 2;
  const is_left = x < w / 2;
  if ( is_top && is_left ) return 1;
  if ( is_top && !is_left ) return 2;
  if ( !is_top && is_left ) return 3;
  return 4;
};

const resetTapCode = (): void =>
{
  tap_code_index = 0;
  tap_code_start_ts = 0;
};

const registerTapCodeStep = ( quadrant: number ): void =>
{
  const code = DebugSettings.DEBUG_CONSOLE_TAP_CODE;
  const window_ms = DebugSettings.DEBUG_CONSOLE_TAP_CODE_WINDOW_MS;
  if ( !code || code.length === 0 ) return;

  const now = performance.now();
  if ( tap_code_index === 0 )
  {
    tap_code_start_ts = now;
  }
  else if ( now - tap_code_start_ts > window_ms )
  {
    tap_code_index = 0;
    tap_code_start_ts = now;
  }

  if ( quadrant !== code[ tap_code_index ] )
  {
    tap_code_index = 0;
    tap_code_start_ts = now;
    if ( quadrant !== code[ 0 ] ) return;
  }

  if ( quadrant === code[ tap_code_index ] )
  {
    tap_code_index += 1;
    if ( tap_code_index >= code.length )
    {
      resetTapCode();
      if ( GlobalSettings.IS_PROD )
        DebugConsoleManager.promptPassword();
      else
        DebugConsoleManager.open();
    }
  }
};

if ( !GlobalSettings.IS_PROD )
{
  ( window as any ).__perf = {
    bodies: () =>
    {
      let n = 0;
      PhysicsWorld.getWorld().forEachRigidBody( () => { n++; } );
      return n;
    },
    liveDebris: () => VfxBlockShatterTask.hasLiveDebris(),
    shadowDebug: () =>
    {
      const d = { ...GameScreen.shadow_debug };
      GameScreen.shadow_debug = { frames: 0, refresh: 0, cannon: 0, count: 0, debris: 0, entities: 0 };
      return d;
    },
    state: () => ( {
      shadows: Renderer.renderer?.shadowMap.enabled ?? null,
      shadow_preset: SHADOW_PRESETS[diag_shadow_index].label,
      recv_shadows: diag_receive_shadows,
      ccd: diag_ccd,
      solver_iters: diag_solver_iters,
      clearcoat: PixelFeatures.clearcoat,
      normal_maps: PixelFeatures.normal_maps,
      emissive_maps: PixelFeatures.emissive_maps,
      materials: MATERIAL_MODES[diag_material_mode],
      lights: LIGHT_MODES[diag_light_mode],
      front_light: !GlobalSettings.DEBUG_NO_FRONT_LIGHT,
      vfx3d: !GlobalSettings.DEBUG_DISABLE_VFX3D,
      ball_shadow: CannonBall.cast_shadow,
      autofire: StressTest.isAutofiring(),
      stress_index: StressTest.getIndex(),
    } ),
    setShadows: ( on: boolean ) => { if ( ( Renderer.renderer?.shadowMap.enabled ?? false ) !== on ) diagToggleShadows(); },
    setRecvShadows: ( on: boolean ) => { if ( diag_receive_shadows !== on ) diagToggleReceiveShadows(); },
    setCcd: ( on: boolean ) => { if ( diag_ccd !== on ) diagToggleCcd(); },
    setClearcoat: ( on: boolean ) => { if ( PixelFeatures.clearcoat !== on ) diagToggleClearcoat(); },
    setNormalMaps: ( on: boolean ) => { if ( PixelFeatures.normal_maps !== on ) diagToggleNormalMaps(); },
    setEmissiveMaps: ( on: boolean ) => { if ( PixelFeatures.emissive_maps !== on ) diagToggleEmissiveMaps(); },
    setBallShadow: ( on: boolean ) => { CannonBall.cast_shadow = on; },
    setVfx3d: ( on: boolean ) =>
    {
      GlobalSettings.DEBUG_DISABLE_VFX3D = !on;
      if ( !on ) Vfx3DRuntime.killAllBatches();
    },
    setMaterials: ( mode: string ) =>
    {
      let guard = 0;
      while ( MATERIAL_MODES[diag_material_mode] !== mode && guard++ < MATERIAL_MODES.length )
        diagCycleMaterials();
    },
    setLights: ( mode: string ) =>
    {
      let guard = 0;
      while ( LIGHT_MODES[diag_light_mode] !== mode && guard++ < LIGHT_MODES.length )
        diagCycleLights();
    },
    setShadowPreset: ( label: string ) =>
    {
      let guard = 0;
      while ( SHADOW_PRESETS[diag_shadow_index].label !== label && guard++ < SHADOW_PRESETS.length )
        diagCycleShadowQuality();
    },
    setSolverIters: () => diagToggleSolverIters(),
    setAutofire: ( on: boolean ) => StressTest.setAutofire( on ),
    benchOpen: () => TriangleBench.open(),
    benchClose: () => TriangleBench.close(),
    benchActive: () => TriangleBench.isActive(),
    benchStats: () => TriangleBench.getStats(),
    benchLost: () => TriangleBench.isContextLost(),
    benchViewport: () => TriangleBench.viewportInfo(),
    benchEvents: () => TriangleBench.contextEvents(),
    benchTexture: ( size: number ) => TriangleBench.setTexture( size ),
    benchSlot: ( slot: string, on: boolean ) => TriangleBench.setSlot( slot as never, on ),
    benchReset: () => TriangleBench.resetStats(),
    benchCount: ( n: number ) => TriangleBench.setCount( n ),
    benchSize: ( s: number ) => TriangleBench.setSize( s ),
    benchMaterial: ( m: string ) => TriangleBench.setMaterial( m as never ),
    benchSpin: ( on: boolean ) => TriangleBench.setSpin( on ),
    benchShadows: ( on: boolean ) => TriangleBench.setShadows( on ),
    benchLights: ( kind: string, n: number ) => TriangleBench.setLights( kind as never, n ),
    plainBall: ( on: boolean ) => { CannonBall.force_plain = on; },
    mapsCreated: () => VfxBlockShatterTask.mapsCreated(),
    debrisBodies: ( n: number | null ) => { VfxBlockShatterTask.promoted_override = n; },
    maxSteps: ( n: number ) => { Game.max_physics_steps = n; },
    physBudget: ( ms: number ) => { Game.physics_budget_ms = ms; },
    physAdaptive: ( on: boolean ) =>
    {
      Game.physics_adaptive = on;
      if ( !on ) Game.physics_slow_mode = false;
    },
    physSlow: ( on: boolean ) => { Game.physics_slow_mode = on; },
    physState: () => ( {
      max_steps: Game.max_physics_steps,
      budget_ms: Game.physics_budget_ms,
      adaptive: Game.physics_adaptive,
      slow_mode: Game.physics_slow_mode,
    } ),
    solverIters: ( n: number ) =>
    {
      PhysicsWorld.getWorld().numSolverIterations = n;
      PhysicsWorld.getWorld().forEachRigidBody( body => body.setAdditionalSolverIterations( 0 ) );
    },
    ccdSubsteps: ( n: number ) => { PhysicsWorld.getWorld().maxCcdSubsteps = n; },
    physCounts: () => PhysicsWorld.counts(),
    physThread: () => PhysicsWorld.threadState(),
    meshBatchEnable: ( on: boolean ) => MeshBatcher.setEnabled( on ),
    shadowBatchEnable: ( on: boolean ) => { ShadowBatcher.setEnabled( on ); Renderer.requestShadowUpdate(); },
    shadowBatchStats: () => ( { enabled: ShadowBatcher.isEnabled(), ...ShadowBatcher.last } ),
    meshBatchStats: () => MeshBatcher.stats(),
    meshBatchBench: ( frames: number = 60 ) =>
    {
      const gl = Renderer.renderer.getContext();
      const was = MeshBatcher.isEnabled();
      const measure = ( on: boolean ) =>
      {
        MeshBatcher.setEnabled( on );
        for ( let i = 0; i < 5; i++ )
          Renderer.render( SceneManager.scene, SceneManager.camera );
        gl.finish();
        let cpu = 0;
        const started = performance.now();
        for ( let i = 0; i < frames; i++ )
        {
          const frame_started = performance.now();
          Renderer.render( SceneManager.scene, SceneManager.camera );
          cpu += performance.now() - frame_started;
          gl.finish();
        }
        const ms = ( performance.now() - started ) / frames;
        return { ms_per_frame: Number( ms.toFixed( 3 ) ), cpu_ms_per_frame: Number( ( cpu / frames ).toFixed( 3 ) ), draw_calls: Renderer.renderer.info.render.calls, triangles: Renderer.renderer.info.render.triangles, ...MeshBatcher.stats() };
      };
      const off = measure( false );
      const on = measure( true );
      MeshBatcher.setEnabled( was );
      return { off, on };
    },
    meshBatchDraws: ( on: boolean ) =>
    {
      const renderer = Renderer.renderer as unknown as { renderBufferDirect: ( ...args: unknown[] ) => void };
      const original = renderer.renderBufferDirect;
      const was = MeshBatcher.isEnabled();
      const draws: Record<string, number> = {};
      MeshBatcher.setEnabled( on );
      Renderer.render( SceneManager.scene, SceneManager.camera );
      renderer.renderBufferDirect = function ( this: unknown, ...args: unknown[] )
      {
        const object = args[4] as THREE.Object3D & { isInstancedMesh?: boolean; material?: THREE.Material | THREE.Material[] };
        const material = args[3] as THREE.Material;
        const key = `${object.isInstancedMesh ? "inst" : "plain"}:${material.transparent ? "T" : "O"}:${object.name || object.parent?.name || "?"}`;
        draws[key] = ( draws[key] ?? 0 ) + 1;
        return original.apply( this, args );
      };
      Renderer.renderer.shadowMap.needsUpdate = true;
      Renderer.render( SceneManager.scene, SceneManager.camera );
      renderer.renderBufferDirect = original;
      MeshBatcher.setEnabled( was );
      return Object.entries( draws ).sort( ( a, b ) => b[1] - a[1] );
    },
    meshBatchDiff: ( first: boolean = false, second: boolean = true ) =>
    {
      const gl = Renderer.renderer.getContext();
      const w = gl.drawingBufferWidth;
      const h = gl.drawingBufferHeight;
      const was = MeshBatcher.isEnabled();
      const frame = ( on: boolean ): Uint8Array =>
      {
        MeshBatcher.setEnabled( on );
        Renderer.renderer.shadowMap.needsUpdate = true;
        Renderer.render( SceneManager.scene, SceneManager.camera );
        const pixels = new Uint8Array( w * h * 4 );
        gl.readPixels( 0, 0, w, h, gl.RGBA, gl.UNSIGNED_BYTE, pixels );
        return pixels;
      };
      const plain = frame( first );
      const batched = frame( second );
      MeshBatcher.setEnabled( was );
      let changed = 0;
      let max = 0;
      const cells: Record<string, number> = {};
      for ( let i = 0; i < plain.length; i += 4 )
      {
        const d = Math.max( Math.abs( plain[i] - batched[i] ), Math.abs( plain[i + 1] - batched[i + 1] ), Math.abs( plain[i + 2] - batched[i + 2] ) );
        if ( d > 0 )
        {
          changed++;
          const px = ( i / 4 ) % w;
          const py = h - 1 - Math.floor( i / 4 / w );
          const cell = `${Math.floor( px / 40 ) * 40},${Math.floor( py / 40 ) * 40}`;
          cells[cell] = ( cells[cell] ?? 0 ) + 1;
        }
        max = Math.max( max, d );
      }
      return { pixels: w * h, w, h, changed, max, cells, stats: MeshBatcher.stats() };
    },
    vfxStats: () => Vfx2DRuntime.getDetailedStats(),
    openResultOverlay: ( stars: number = 3 ) => OverlayFactory.openOverlay( OverlayId.GAME_RESULT_OVERLAY, { title_key: "RESULT.LEVEL_PASSED", stars, rewards: [ { currency: "coin", amount: 120 } ] } ),
    openShopOverlay: () => OverlayFactory.openOverlay( OverlayId.SHOP_OVERLAY, { onShopClosed: () => {} } ),
    openAvatarDialog: () => { DialogManager.openDialog( new AvatarSelectionDialog( () => {} ) ); },
    openProfileDialog: () => { DialogManager.openDialog( new ProfileDialog() ); },
    openSettingsDialog: () => { DialogManager.openDialog( new SettingsDialog() ); },
    profStart: () => FpsOverlay.profileStart(),
    profStop: () => FpsOverlay.profileStop(),
    profStats: () => FpsOverlay.profileStats(),
    rimMode: ( mode: string ) => RimLightTest.setMode( mode as never ),
    rimColor: ( hex: number ) => RimLightTest.setColor( hex ),
    rimIntensity: ( value: number ) => RimLightTest.setIntensity( value ),
    rimState: () => RimLightTest.getState(),
    rimReset: () => RimLightTest.reset(),
    inspectorOpen: () => openMaterialInspector(),
    ballTuner: () => BallTuner.toggle(),
    ballSpeed: ( value: number ) => BallTuner.setSpeed( value ),
    ballMass: ( value: number ) => BallTuner.setMass( value ),
    ballReset: () => BallTuner.reset(),
    blockMass: ( value: number ) => TextureTest.setBlockMass( value ),
    blockMassState: () => ( {
      block: BlockMassTuner.getActiveKey(),
      base: BlockMassTuner.getBaseMass(),
      mass: BlockMassTuner.getMass(),
      modified: BlockMassTuner.modifiedCount(),
    } ),
    blockMassReset: () => TextureTest.resetMasses(),
    inspectorReset: () => ( window as any ).__inspector?.reset(),
    textureCount: () => TextureTest.COUNT,
    textureNames: () => TextureTest.names(),
    textureIndex: () => TextureTest.getIndex(),
    textureName: () => TextureTest.getName(),
    textureRun: ( index: number ) =>
    {
      const level = TextureTest.activate( index );
      void ScreenManager.showScreen( Screens.GAME, { level } );
    },
    textureNext: () =>
    {
      void ScreenManager.showScreen( Screens.GAME, { level: TextureTest.advance() } );
    },
    stressRun: ( index: number ) =>
    {
      const level = StressTest.activate( index );
      void ScreenManager.showScreen( Screens.GAME, { level } );
    },
    replay: () =>
    {
      void ScreenManager.showScreen( Screens.GAME, { level: StressTest.currentLevel() } );
    },
    goLevel: ( level: number ) =>
    {
      DebugConsoleManager.close();
      Game.instance.hideLoadingGameScreen();
      void ScreenManager.showScreen( Screens.GAME, { level } );
    },
    bhState: () => BoosterHighlightRenderer.diagnostics(),
    bhReset: () => BoosterHighlightRenderer.resetDiagnostics(),
  };
}

let inspector_loading = false;

async function openMaterialInspector(): Promise<void>
{
  const api = ( window as any ).__inspector;
  if ( api )
  {
    api.show();
    return;
  }

  if ( inspector_loading ) return;
  inspector_loading = true;

  try
  {
    const response = await fetch( `inspector.js?v=${Date.now()}` );
    if ( !response.ok ) throw new Error( `HTTP ${response.status}` );
    const source = await response.text();
    ( 0, eval )( source );
    ( window as any ).__inspector?.show();
  }
  catch ( error )
  {
    console.error( "[MaterialInspector] не загрузился:", error );
  }
  finally
  {
    inspector_loading = false;
  }
}

function runTextureLevel( index: number ): void
{
  const level = TextureTest.activate( index );
  DebugConsoleManager.close();
  Game.instance.hideLoadingGameScreen();
  void ScreenManager.showScreen( Screens.GAME, { level } );
}

TextureTest.show_level = ( level: number ) =>
{
  void ScreenManager.showScreen( Screens.GAME, { level } );
};

function runStressLevel( index: number ): void
{
  const level = StressTest.activate( index );
  DebugConsoleManager.close();
  void ScreenManager.showScreen( Screens.GAME, { level } );
}

function runTableFormationLevel( index: number ): void
{
  const level = TableFormationShowcase.activate( index );
  DebugConsoleManager.close();
  Game.instance.hideLoadingGameScreen();
  void ScreenManager.showScreen( Screens.GAME, { level } );
}

type ButtonConfig = { text?: string; getText?: () => string; action: () => void };
type SectionConfig = { title: string; buttons: ButtonConfig[] };
type DebugPage = { title: string; sections: SectionConfig[] };

function buildPanel(): HTMLDivElement
{
  const overlay = document.createElement( "div" );
  overlay.id = "debugConsolePanel";
  overlay.style.position = "fixed";
  overlay.style.left = "0";
  overlay.style.top = "0";
  overlay.style.right = "0";
  overlay.style.bottom = "0";
  overlay.style.zIndex = "2147483647";
  overlay.style.background = "rgba(10, 12, 20, 0.94)";
  overlay.style.color = "#fff";
  overlay.style.font = "13px Arial, sans-serif";
  overlay.style.overflowY = "auto";
  overlay.style.boxSizing = "border-box";
  // Keep the CLOSE header out from under the notch / Telegram top bar.
  // --tg-safe-* come from TelegramAPISettings.applySafeAreaCssVariables (Telegram insets merged with env()).
  overlay.style.paddingTop = "calc(16px + var(--tg-safe-top, env(safe-area-inset-top, 0px)))";
  overlay.style.paddingBottom = "calc(16px + var(--tg-safe-bottom, env(safe-area-inset-bottom, 0px)))";
  overlay.style.paddingLeft = "calc(16px + var(--tg-safe-left, env(safe-area-inset-left, 0px)))";
  overlay.style.paddingRight = "calc(16px + var(--tg-safe-right, env(safe-area-inset-right, 0px)))";
  overlay.style.display = "none";

  const header = document.createElement( "div" );
  header.style.display = "flex";
  header.style.justifyContent = "space-between";
  header.style.alignItems = "center";
  header.style.marginBottom = "16px";

  const title = document.createElement( "div" );
  title.textContent = "DEBUG CONSOLE";
  title.style.font = "700 18px Arial, sans-serif";

  const close_btn = document.createElement( "button" );
  close_btn.type = "button";
  close_btn.textContent = "CLOSE";
  styleButton( close_btn, "#c62828" );
  close_btn.style.minHeight = "56px";
  close_btn.style.padding = "16px 18px";
  close_btn.style.fontSize = "14px";
  close_btn.addEventListener( "click", () => DebugConsoleManager.close() );

  header.appendChild( title );
  header.appendChild( close_btn );
  overlay.appendChild( header );

  const sections: SectionConfig[] = [
    {
      title: "SCREENS",
      buttons: [
        { text: "MENU", action: () => { Game.instance.hideLoadingGameScreen(); void ScreenManager.showScreen( Screens.MENU ); } },
        { text: "LEADERBOARD", action: () => { Game.instance.hideLoadingGameScreen(); void ScreenManager.showScreen( Screens.LEADERBOARD ); } },
        { text: "FRIENDS", action: () => { Game.instance.hideLoadingGameScreen(); void ScreenManager.showScreen( Screens.FRIENDS ); } },
        { text: "SETTINGS", action: () => Game.instance.openSettingsDialog() },
        { text: "LOADING GAME", action: () => { DebugConsoleManager.close(); Game.instance.showLoadingGameScreen(); } },
        { text: "LOADING CONVEYOR", action: () => { DebugConsoleManager.close(); Game.instance.showLoadingGameScreen( false, "conveyor" ); } },
      ],
    },
    {
      title: "SAFE AREA",
      buttons: [
        { text: "SHOW SAFE AREA INSETS", action: () =>
        {
          TelegramAPISettings.applySafeAreaCssVariables();
          const info = TelegramAPISettings.getInsetsDebugInfo();
          void navigator.clipboard?.writeText( info ).catch( () => {} );
          alert( info.replace( /,"/g, ',\n"' ) );
        } },
      ],
    },
    {
      title: "GAME STATE",
      buttons: [
        // { text: "REPLAY CART DRIVE", action: () => { DebugConsoleManager.close(); void ScreenManager.showScreen( Screens.MENU ).then( () => MenuScreen.active?.replayCartDrive() ); } },
        { text: "PLAY LEVEL... (no progress)", action: () => goToLevelPrompt() },
        { text: "ОБЗОР ПУЛА УРОВНЕЙ", action: () => { DebugConsoleManager.close(); LevelPoolBrowser.open(); } },
        { getText: () => set_level_inflight ? "SETTING LEVEL..." : "LEVEL +1", action: () => { void shiftLevelOnServer( 1 ); } },
        { getText: () => set_level_inflight ? "SETTING LEVEL..." : "LEVEL -1", action: () => { void shiftLevelOnServer( -1 ); } },
        { getText: () => set_level_inflight ? "SETTING LEVEL..." : "SET LEVEL...", action: () => { void setLevelPrompt(); } },
        { getText: () => add_happy_fall_ball_inflight ? "ADDING BALL..." : "HAPPY FALL +1 BALL", action: () => { void addHappyFallBall(); } },
        { text: "CONTINUE OVERLAY (test)", action: () => {
          DebugConsoleManager.close();
          OverlayFactory.openOverlay( OverlayId.CONTINUE_OVERLAY, {
            offer: { price: 900, shots: 5 }, purchase_index: 0, level: StatisticsStorage.load().level,
            onClose: () => {}, onBuy: () => {},
          } );
        } },
        { text: "DAILY CARD REWARD OVERLAY", action: () => {
          DebugConsoleManager.close();
          OverlayFactory.openOverlay( OverlayId.DAILY_CARD_REWARD_OVERLAY );
        } },
        { text: "LEVEL BOX OPEN OVERLAY", action: () => {
          DebugConsoleManager.close();
          OverlayFactory.openOverlay( OverlayId.LEVEL_BOX_OPEN_OVERLAY );
        } },
        { text: "WHEEL OF FORTUNE OVERLAY", action: () => {
          DebugConsoleManager.close();
          OverlayFactory.openOverlay( OverlayId.WHEEL_OF_FORTUNE_OVERLAY );
        } },
        { getText: () => {
          const stats = StatisticsStorage.load();
          return add_heart_inflight
            ? "ADDING HEART..."
            : `HEART +1 (${stats.hearts}/${stats.max_hearts})`;
        }, action: () => { void addHeartOnServer(); } },
        { text: "TOGGLE SOUNDS", action: () => { const s = StatisticsStorage.load(); StatisticsStorage.update( { sounds_enabled: !s.sounds_enabled } ); updateInfoText(); } },
        { text: "TOGGLE MUSIC", action: () => { const s = StatisticsStorage.load(); StatisticsStorage.update( { music_enabled: !s.music_enabled } ); updateInfoText(); } },
        { text: "NEW USER", action: () => createNewUserAndReload() },
        { getText: () => reset_progress_inflight ? "RESETTING PROGRESS..." : "RESET PROGRESS", action: () => { void resetProgressOnServer(); } },      ],
    },
    {
      title: "BLOCKS",
      buttons: [
        { getText: () => `STRIPE TEST — NORMAL MAPS: ${PixelFeatures.normal_maps ? "ON" : "OFF"}`,
          action: () => diagToggleNormalMaps() },
        ...NewBlockModelsShowcase.TESTS.map( ( test, index ) => ( {
          text: `NEW MODEL: ${test.label}`,
          action: () =>
          {
            const level = NewBlockModelsShowcase.activate( index );
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { level } );
          },
        } ) ),
        { getText: () => `ALL BLOCKS (${BlockShowcase.blockCount()})`, action: () =>
          {
            const level = BlockShowcase.activate();
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { level } );
          } },
        { text: "MINE BOOSTER TEST", action: () =>
          {
            const level = NewBlockModelsShowcase.activate( 2 );
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { level, level_start_booster_id: CumulativeBoosterID.Mine } );
          } },
        { getText: () => `BLOCK NAMES: ${BlockShowcase.labelModeName()}`,
          action: () => BlockShowcase.cycleLabels() },
        { text: "TILTED TABLE LEVEL (tiltDeg test)", action: () =>
          {
            const level = TiltedTableShowcase.activate();
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { level } );
          } },
        { text: "ROTATED TABLE LEVEL (diamond pyramid 5-1)", action: () =>
          {
            const level = RotatedTableShowcase.activate();
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { level } );
          } },
        { text: "SHELF TABLE LEVEL (standOn test)", action: () =>
          {
            const level = ShelfTableShowcase.activate();
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { level } );
          } },
        { text: "HANGING TABLE LEVEL (no-leg swing test)", action: () =>
          {
            const level = HangingTableShowcase.activate();
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { level } );
          } },
        { text: "ELEVATOR TABLE LEVEL (up/down leg test)", action: () =>
          {
            const level = ElevatorTableShowcase.activate();
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { level } );
          } },
        { text: "ESCALATOR LEVEL (belt + lose button)", action: () =>
          {
            StatisticsStorage.update( { conveyor_mirrored: false } );
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { is_conveyor: true } );
          } },
        { text: "ESCALATOR LEVEL, MIRRORED (left-handed)", action: () =>
          {
            StatisticsStorage.update( { conveyor_mirrored: true } );
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { is_conveyor: true } );
          } },
        { text: "SLIDE TABLE LEVEL (left/right deck test)", action: () =>
          {
            const level = SlideTableShowcase.activate();
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { level } );
          } },
        { text: "LEANING LOGS LEVEL (teepee test)", action: () =>
          {
            const level = LeaningLogsShowcase.activate();
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { level } );
          } },
        { text: "TABLE CASE LEVEL (scratch bench)", action: () =>
          {
            const level = TableCaseShowcase.activate();
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { level } );
          } },
        { text: "JAR BAY LEVEL (posts + bars + discs)", action: () =>
          {
            const level = JarBayShowcase.activate();
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { level } );
          } },
        { text: "JAR SANDWICH LEVEL (posts + discs)", action: () =>
          {
            const level = JarSandwichShowcase.activate();
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { level } );
          } },
        { text: "JAR CASES LEVEL (collider bench)", action: () =>
          {
            const level = JarCasesShowcase.activate();
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { level } );
          } },
        { text: "JAR PYRAMID LEVEL", action: () =>
          {
            const level = JarPyramidShowcase.activate();
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { level } );
          } },
        { text: "DIAMOND TOWER LEVEL (rotationDeg test)", action: () =>
          {
            const level = DiamondTowerShowcase.activate();
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { level } );
          } },
      ],
    },
    {
      title: "BARRIERS",
      buttons: BarrierShowcase.TESTS.map( ( test, index ) => ( {
        text: `BARRIER: ${test.label}`,
        action: () =>
        {
          const level = BarrierShowcase.activate( index );
          DebugConsoleManager.close();
          Game.instance.hideLoadingGameScreen();
          void ScreenManager.showScreen( Screens.GAME, { level } );
        },
      } ) ),
    },
    {
      title: "TRIANGLE BENCH (ПУСТАЯ СЦЕНА)",
      buttons: [
        { text: "ОТКРЫТЬ СТЕНД ТРЕУГОЛЬНИКОВ", action: () =>
          {
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.MENU ).then( () => TriangleBench.open() );
          } },
      ],
    },
    {
      title: "ЗАДНИЙ СВЕТ (RIM)",
      buttons: [
        { getText: () => `РЕЖИМ: ${RimLightTest.modeLabel()}`,
          action: () => RimLightTest.cycleMode() },
        { getText: () => `ЦВЕТ: ${RimLightTest.modeLabel()}`,
          action: () => RimLightTest.cycleColor() },
        { text: "СБРОС ЗАДНЕГО СВЕТА", action: () => RimLightTest.reset() },
      ],
    },
    {
      title: "ЯДРО (СИЛА / МАССА)",
      buttons: [
        { getText: () => `ПАНЕЛЬ ЯДРА: ${BallTuner.stateLabel()}`, action: () =>
          {
            DebugConsoleManager.close();
            BallTuner.open();
          } },
        { text: "СБРОСИТЬ ЯДРО", action: () => BallTuner.reset() },
      ],
    },
    {
      title: "MATERIAL INSPECTOR (РЕГУЛИРОВКА БЛОКОВ)",
      buttons: [
        { text: "ОТКРЫТЬ ИНСПЕКТОР МАТЕРИАЛОВ", action: () =>
          {
            DebugConsoleManager.close();
            void openMaterialInspector();
          } },
        { text: "СБРОСИТЬ ВСЁ", action: () => ( window as any ).__inspector?.reset() },
        { text: "ВЫГРУЗИТЬ ИНСПЕКТОР", action: () => ( window as any ).__inspector?.remove() },
      ],
    },
    {
      title: "TEXTURE TEST (ONE BLOCK PER LEVEL)",
      buttons: [
        { getText: () => `RUN #${TextureTest.getIndex() + 1}/${TextureTest.COUNT}: ${TextureTest.getName()}`,
          action: () => runTextureLevel( TextureTest.getIndex() ) },
        { getText: () => `NEXT  →  #${( TextureTest.getIndex() + 1 ) % TextureTest.COUNT + 1}: ${TextureTest.getName( TextureTest.getIndex() + 1 )}`,
          action: () => runTextureLevel( TextureTest.getIndex() + 1 ) },
        { getText: () => `PREV  ←  #${( TextureTest.getIndex() - 1 + TextureTest.COUNT ) % TextureTest.COUNT + 1}: ${TextureTest.getName( TextureTest.getIndex() - 1 )}`,
          action: () => runTextureLevel( TextureTest.getIndex() - 1 ) },
        { text: "FROM THE FIRST TEXTURE", action: () => runTextureLevel( 0 ) },
        { getText: () => `МАССЫ БЛОКОВ → ДЕФОЛТ (${BlockMassTuner.modifiedCount()})`,
          action: () => TextureTest.resetMasses() },
        { getText: () => `TEXTURE MODE: ${TextureTest.isActive() ? "ON" : "OFF"}`,
          action: () =>
          {
            if ( TextureTest.isActive() )
            {
              TextureTest.deactivate();
              DebugConsoleManager.close();
              void ScreenManager.showScreen( Screens.MENU );
            }
            else
            {
              runTextureLevel( TextureTest.getIndex() );
            }
          } },
      ],
    },
    {
      title: "TABLE LAYOUTS (2-4 TABLES, EVERY FORMATION)",
      buttons: [
        { getText: () => `RUN #${TableFormationShowcase.getIndex() + 1}/${TableFormationShowcase.COUNT}: ${TableFormationShowcase.getName()}`,
          action: () => runTableFormationLevel( TableFormationShowcase.getIndex() ) },
        { getText: () => `NEXT  →  #${( TableFormationShowcase.getIndex() + 1 ) % TableFormationShowcase.COUNT + 1}: ${TableFormationShowcase.getName( TableFormationShowcase.getIndex() + 1 )}`,
          action: () => runTableFormationLevel( TableFormationShowcase.getIndex() + 1 ) },
        { getText: () => `PREV  ←  #${( TableFormationShowcase.getIndex() - 1 + TableFormationShowcase.COUNT ) % TableFormationShowcase.COUNT + 1}: ${TableFormationShowcase.getName( TableFormationShowcase.getIndex() - 1 )}`,
          action: () => runTableFormationLevel( TableFormationShowcase.getIndex() - 1 ) },
        { text: "FROM THE FIRST LAYOUT", action: () => runTableFormationLevel( 0 ) },
        { getText: () => `LAYOUT MODE: ${TableFormationShowcase.isActive() ? "ON" : "OFF"}`,
          action: () =>
          {
            if ( TableFormationShowcase.isActive() )
            {
              TableFormationShowcase.deactivate();
              DebugConsoleManager.close();
              void ScreenManager.showScreen( Screens.MENU );
            }
            else
            {
              runTableFormationLevel( TableFormationShowcase.getIndex() );
            }
          } },
      ],
    },
    {
      title: "STRESS TEST (FPS)",
      buttons: [
        { getText: () => `RUN #${StressTest.getIndex() + 1}/${StressTest.COUNT}: ${StressTest.getName()}`,
          action: () => runStressLevel( StressTest.getIndex() ) },
        { getText: () => `BLOCKS: ${StressTest.getBlockCount()}  →  NEXT #${( StressTest.getIndex() + 1 ) % StressTest.COUNT + 1}`,
          action: () => runStressLevel( StressTest.getIndex() + 1 ) },
        { text: "REPLAY CURRENT", action: () => runStressLevel( StressTest.getIndex() ) },
        { getText: () => `AUTO FIRE: ${StressTest.isAutofiring() ? "ON" : "OFF"}`,
          action: () =>
          {
            StressTest.toggleAutofire();
            DebugConsoleManager.close();
          } },
        { text: "RUN + AUTO FIRE", action: () =>
          {
            StressTest.setAutofire( true );
            runStressLevel( StressTest.getIndex() );
          } },
        { getText: () => `STRESS MODE: ${StressTest.isActive() ? "ON" : "OFF"}`,
          action: () =>
          {
            if ( StressTest.isActive() )
            {
              StressTest.deactivate();
              DebugConsoleManager.close();
              void ScreenManager.showScreen( Screens.MENU );
            }
            else
            {
              runStressLevel( StressTest.getIndex() );
            }
          } },
      ],
    },
    {
      title: "ENVIRONMENT",
      buttons: [
        { getText: () => `SERVER ENV: ${getRuntimeEnvironmentLabel()}`, action: () => toggleRuntimeEnvironment() },
        { text: "CHECK SERVER ROUTING", action: () => void checkServerRouting() },
        { getText: () => `CLEAR ROUTING OVERRIDE${getServerRoutingOverride() ? "" : " (none)"}`, action: () => clearServerRoutingOverride() },
      ],
    },
    ...( OfferService.isPurchaseSimulationAvailable() ? [ {
      title: "OFFERS (TEST ONLY)",
      buttons: [
        {
          getText: () => `SIMULATE PURCHASE: ${OfferService.isPurchaseSimulationEnabled() ? "ON" : "OFF"} (TEST)`,
          action: () => OfferService.setPurchaseSimulationEnabled( !OfferService.isPurchaseSimulationEnabled() ),
        },
      ],
    } ] : [] ),
    {
      title: "ADS (APPLOVIN MAX)",
      buttons: [
        { text: "MAX MEDIATION DEBUGGER", action: () => { DebugConsoleManager.close(); void MaxAds.showMediationDebugger(); } },
        { text: "SHOW INTERSTITIAL", action: () => { DebugConsoleManager.close(); void MaxAds.tryShowInterstitial( "debug", 1 ); } },
        { text: "SHOW REWARDED", action: () => { DebugConsoleManager.close(); void MaxAds.tryShowRewarded( "debug", 1 ); } },
      ],
    },
    {
      title: "LOCALIZATION",
      buttons: [
        { getText: () => `LANGUAGE: ${LocalizationManager.getCurrentLanguage().toUpperCase()}`, action: () => cycleLanguage() },
      ],
    },
    {
      title: "RESULT OVERLAY",
      buttons: [
        { text: "WIN 3★", action: () => { DebugConsoleManager.close(); OverlayFactory.openOverlay( OverlayId.GAME_RESULT_OVERLAY, { title_key: "RESULT.LEVEL_PASSED", stars: 3, rewards: [ { currency: "coin", amount: 120 } ] } ); } },
        { text: "WIN 2★ + HEART", action: () => { DebugConsoleManager.close(); OverlayFactory.openOverlay( OverlayId.GAME_RESULT_OVERLAY, { title_key: "RESULT.LEVEL_PASSED", stars: 2, rewards: [ { currency: "coin", amount: 80 }, { currency: "heart", amount: 1 } ] } ); } },
        { text: "WIN 1★", action: () => { DebugConsoleManager.close(); OverlayFactory.openOverlay( OverlayId.GAME_RESULT_OVERLAY, { title_key: "RESULT.LEVEL_PASSED", stars: 1, rewards: [ { currency: "coin", amount: 40 } ] } ); } },
        { text: "LOSE", action: () => { DebugConsoleManager.close(); OverlayFactory.openOverlay( OverlayId.GAME_RESULT_OVERLAY, { title_key: "RESULT.LEVEL_FAILED", stars: 0, rewards: [] } ); } },
        { text: "CONVEYOR INTRO", action: () => { DebugConsoleManager.close(); Game.instance.showLoadingGameScreen( false, "conveyor" ); void Game.instance.waitForLoadingGamePlay().then( () => { Game.instance.hideLoadingGameScreen(); void ScreenManager.showScreen( Screens.MENU ); } ); } },
        { text: "CONVEYOR LEVEL PASSED", action: () => { DebugConsoleManager.close(); OverlayFactory.openOverlay( OverlayId.CONVEYOR_RESULT_OVERLAY, { won: true, level: 1, seconds: 20, time_limit: 20, rewards: [ { type: "coin", amount: 1000 } ], stats_before: StatisticsStorage.load(), onExit: () => {}, onContinue: () => {} } ); } },
        { text: "CONVEYOR GAME OVER", action: () => { DebugConsoleManager.close(); OverlayFactory.openOverlay( OverlayId.CONVEYOR_RESULT_OVERLAY, { won: false, level: 1, seconds: 24, time_limit: 25, rewards: [], stats_before: StatisticsStorage.load(), onExit: () => {}, onContinue: () => {} } ); } },
        { text: "CONVEYOR RETRY", action: () => { DebugConsoleManager.close(); OverlayFactory.openOverlay( OverlayId.CONTINUE_OVERLAY, { offer: { price: 600, shots: 0 }, purchase_index: 0, conveyor_retry: () => Promise.resolve( false ) } ); } },
      ],
    },
    {
      title: "OVERLAYS",
      buttons: [
        { text: "INFINITE LIVES (15 MIN)", action: () => { DebugConsoleManager.close(); OverlayFactory.openOverlay( OverlayId.INFINITE_LIVES_OVERLAY, { duration_minutes: 15 } ); } },
        { getText: () => `MEM OVERLAY: ${MemoryOverlay.isVisible() ? "ON" : "OFF"}`, action: () => MemoryOverlay.toggle() },
        { getText: () => `FPS OVERLAY: ${GlobalSettings.SHOW_FPS_OVERLAY ? "ON" : "OFF"}`, action: () => Game.instance.setFpsOverlayEnabled( !GlobalSettings.SHOW_FPS_OVERLAY ) },
        { getText: () => `LOG OVERLAY: ${LogOverlay.isVisible() ? "ON" : "OFF"}`, action: () => LogOverlay.toggle() },
        { getText: () => `PHYSICS COLLIDERS: ${PhysicsDebugRenderer.isEnabled() ? "ON" : "OFF"}`, action: () => PhysicsDebugRenderer.setEnabled( !PhysicsDebugRenderer.isEnabled() ) },
      ],
    },
    {
      title: "PERF DIAG",
      buttons: [
        { getText: () => `SHADOWS: ${Renderer.renderer?.shadowMap.enabled ? "ON" : "OFF"}`, action: () => diagToggleShadows() },
        { getText: () => `RENDER SCALE: ${diag_render_scales[diag_scale_index].toFixed( 2 )}x`, action: () => diagCycleRenderScale() },
        { getText: () => `SOLVER ITERS: ${diag_solver_iters}`, action: () => diagToggleSolverIters() },
        { getText: () => `MESH BATCHING: ${MeshBatcher.isEnabled() ? "ON" : "OFF"}`, action: () => MeshBatcher.setEnabled( !MeshBatcher.isEnabled() ) },
        { getText: () => `SHADOW BATCHING: ${ShadowBatcher.isEnabled() ? "ON" : "OFF"}`, action: () => { ShadowBatcher.setEnabled( !ShadowBatcher.isEnabled() ); Renderer.requestShadowUpdate(); } },
        { getText: () => `MSAA: ${localStorage.getItem( "perf_diag_no_msaa" ) === "1" ? "OFF" : "ON"} (RELOAD)`, action: () =>
          {
            localStorage.setItem( "perf_diag_no_msaa", localStorage.getItem( "perf_diag_no_msaa" ) === "1" ? "0" : "1" );
            window.location.reload();
          } },
        { getText: () => `SHADOW: ${SHADOW_PRESETS[diag_shadow_index].label}`, action: () => diagCycleShadowQuality() },
        { getText: () => `RECV SHADOWS: ${diag_receive_shadows ? "ON" : "OFF"}`, action: () => diagToggleReceiveShadows() },
        { getText: () => `CCD: ${diag_ccd ? "ON" : "OFF"}`, action: () => diagToggleCcd() },
        { getText: () => `FRONT LIGHT: ${GlobalSettings.DEBUG_NO_FRONT_LIGHT ? "OFF" : "ON"}`, action: () => diagToggleFrontLight() },
        { getText: () => `LIGHTS: ${LIGHT_MODES[diag_light_mode]}`, action: () => diagCycleLights() },
        { getText: () => `MATERIALS: ${MATERIAL_MODES[diag_material_mode]}`, action: () => diagCycleMaterials() },
        { getText: () => `CLEARCOAT: ${PixelFeatures.clearcoat ? "ON" : "OFF"}`, action: () => diagToggleClearcoat() },
        { getText: () => `NORMAL MAPS: ${PixelFeatures.normal_maps ? "ON" : "OFF"}`, action: () => diagToggleNormalMaps() },
        { getText: () => `EMISSIVE MAPS: ${PixelFeatures.emissive_maps ? "ON" : "OFF"}`, action: () => diagToggleEmissiveMaps() },
        { getText: () => `BALL SHADOW: ${CannonBall.cast_shadow ? "ON" : "OFF"}`, action: () => { CannonBall.cast_shadow = !CannonBall.cast_shadow; } },
        { getText: () => `VFX 3D: ${GlobalSettings.DEBUG_DISABLE_VFX3D ? "OFF" : "ON"}`, action: () =>
          {
            GlobalSettings.DEBUG_DISABLE_VFX3D = !GlobalSettings.DEBUG_DISABLE_VFX3D;
            if ( GlobalSettings.DEBUG_DISABLE_VFX3D )
              Vfx3DRuntime.killAllBatches();
          } },
        { text: "JAM SPLASH TEST", action: () =>
          {
            DebugConsoleManager.close();
            const camera = SceneManager.camera;
            const target = camera.getWorldDirection( new THREE.Vector3() )
              .multiplyScalar( 14 )
              .add( camera.position );
            Vfx3DManager.addJamSplash( target, 0.5, 1, 0 );
            const physics_particles = Vfx3DRuntime.getBatch( VfxPhysicsParticleBatch );
            for ( const at of [ 100, 400, 800 ] )
            {
              setTimeout( () => console.log(
                `[JAM TEST ${at}ms] particles=${physics_particles.debugInstanceCount()}`
                + ` vfxtime=${Vfx3DRuntime.current_time.toFixed( 2 )}`
              ), at );
            }
          } },
        { text: "EMPTY LEVEL", action: () =>
          {
            GlobalSettings.DEBUG_EMPTY_LEVEL = true;
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { level: StatisticsStorage.load().level } );
          } },
      ],
    },
    {
      title: "BOOSTERS (GRANT TO SELF)",
      buttons: [
        { getText: () => `ROCKET (FIREWORKS) ×1  →  have ${activeGameScreen()?.getBoosterCount( CumulativeBoosterID.Fireworks ) ?? "?"}`,
          action: () => grantBoosterUse( CumulativeBoosterID.Fireworks ) },
        { getText: () => `MULTIBALL ×1  →  have ${activeGameScreen()?.getBoosterCount( CumulativeBoosterID.Multiball ) ?? "?"}`,
          action: () => grantBoosterUse( CumulativeBoosterID.Multiball ) },
        { getText: () => `BIG BALL ×1  →  have ${activeGameScreen()?.getBoosterCount( CumulativeBoosterID.BigBall ) ?? "?"}`,
          action: () => grantBoosterUse( CumulativeBoosterID.BigBall ) },
        { getText: () => `INFINITE BALLS ×1  →  have ${activeGameScreen()?.getBoosterCount( CumulativeBoosterID.InfiniteBalls ) ?? "?"}`,
          action: () => grantBoosterUse( CumulativeBoosterID.InfiniteBalls ) },
      ],
    },
    {
      title: "DIALOGS",
      buttons: [
        { text: "FIRST STEPS OVERLAY", action: () => { DebugConsoleManager.close(); OverlayFactory.openOverlay( OverlayId.FIRST_STEPS_OVERLAY, { state: FIRST_STEPS_PREVIEW_STATE, preview: true, on_go: ( task: FirstStepsTaskDto ) => MenuScreen.active?.goToFirstStepsTask( task, FIRST_STEPS_PREVIEW_STATE ) } ); } },
        { text: "FIRST STEPS OVERLAY (ALL DONE)", action: () => { DebugConsoleManager.close(); OverlayFactory.openOverlay( OverlayId.FIRST_STEPS_OVERLAY, { state: FIRST_STEPS_PREVIEW_DONE_STATE, preview: true } ); } },
        { text: "FIRST STEPS OVERLAY (TASK DONE ANIM)", action: () => { DebugConsoleManager.close(); OverlayFactory.openOverlay( OverlayId.FIRST_STEPS_OVERLAY, { state: FIRST_STEPS_PREVIEW_FRESH_STATE, preview: true, fresh_task_ids: FIRST_STEPS_PREVIEW_FRESH_IDS } ); } },
        { text: "WHEEL OF FORTUNE (NO SPINS)", action: () => { DebugConsoleManager.close(); OverlayFactory.openOverlay( OverlayId.WHEEL_OF_FORTUNE_OVERLAY, { rewarded: false } ); } },
        { text: "CONVEYOR TUTORIAL", action: () => { DebugConsoleManager.close(); ConveyorTutorial.reset(); MenuScreen.active?.startConveyorTutorial(); } },
        { text: "HAPPY FALL RESULT (NO PRIZES)", action: () => { DebugConsoleManager.close(); void ImagesHolder.ensure( ImgID.UI_HappyFallRaccoonNoPrizes ).then( () => OverlayFactory.openOverlay( OverlayId.HAPPY_FALL_RESULT_OVERLAY, { rewards: [], onClaim: () => {} } ) ); } },
        { text: "RETURN GIFT OVERLAY", action: () => { DebugConsoleManager.close(); OverlayFactory.openOverlay( OverlayId.RETURN_GIFT_OVERLAY, { preview_rewards: [ { type: "infinite_hearts", amount: 15 }, { type: "coin", amount: 100 } ] } ); } },
        { text: "NEW BOOSTER FIREWORK", action: () => { DebugConsoleManager.close(); OverlayFactory.openOverlay( OverlayId.NEW_BOOSTER_OVERLAY, { booster_type: BuyBoosterType.Firework } ); } },
        { text: "NEW BOOSTER FIVE BALLS", action: () => { DebugConsoleManager.close(); OverlayFactory.openOverlay( OverlayId.NEW_BOOSTER_OVERLAY, { booster_type: BuyBoosterType.FiveBalls } ); } },
        { text: "NEW BOOSTER BIG BALL", action: () => { DebugConsoleManager.close(); OverlayFactory.openOverlay( OverlayId.NEW_BOOSTER_OVERLAY, { booster_type: BuyBoosterType.BigBall } ); } },
        { text: "NEW BOOSTER INFINITE SHOTS", action: () => { DebugConsoleManager.close(); OverlayFactory.openOverlay( OverlayId.NEW_BOOSTER_OVERLAY, { booster_type: BuyBoosterType.InfiniteShots } ); } },
        { text: "BOOSTER FIREWORK", action: () => { void openDebugBuyBoosterDialog( BuyBoosterType.Firework, CumulativeBoosterID.Fireworks ); } },
        { text: "BOOSTER FIVE BALLS", action: () => { void openDebugBuyBoosterDialog( BuyBoosterType.FiveBalls, CumulativeBoosterID.Multiball ); } },
        { text: "BOOSTER BIG BALL", action: () => { void openDebugBuyBoosterDialog( BuyBoosterType.BigBall, CumulativeBoosterID.BigBall ); } },
        { text: "BOOSTER INFINITE SHOTS", action: () => { void openDebugBuyBoosterDialog( BuyBoosterType.InfiniteShots, CumulativeBoosterID.InfiniteBalls ); } },
        { text: "PLAY HARD", action: () => { DebugConsoleManager.close(); DialogManager.openDialog( new PlayDialog( StatisticsStorage.load().level, PlayDialogState.Play, PlayDialogDifficulty.Hard ) ); } },
        { text: "PLAY SUPER HARD", action: () => { DebugConsoleManager.close(); DialogManager.openDialog( new PlayDialog( StatisticsStorage.load().level, PlayDialogState.Play, PlayDialogDifficulty.SuperHard ) ); } },
        { text: "TRY AGAIN", action: () => { DebugConsoleManager.close(); DialogManager.openDialog( new PlayDialog( StatisticsStorage.load().level, PlayDialogState.TryAgain, PlayDialogDifficulty.Common ) ); } },
        { text: "NO LEVELS", action: () => { DebugConsoleManager.close(); DialogManager.openDialog( new NoLevelsDialog() ); } },
        { text: "UPDATE HARD", action: () => { DebugConsoleManager.close(); DialogManager.openDialog( new UpdateRequiredDialog( UpdateRequiredMode.Hard ) ); } },
        { text: "UPDATE SOFT", action: () => { DebugConsoleManager.close(); DialogManager.openDialog( new UpdateRequiredDialog( UpdateRequiredMode.Soft, 999 ) ); } },
        { text: "CONNECTION FAILED", action: () => {
          DebugConsoleManager.close();
          NetworkManager.handleNetworkError( new Error( "Debug connection failure" ) );
        } },
        { text: "LOADING ERROR", action: () => {
          DebugConsoleManager.close();
          NetworkManager.handleLoadingDataError( new Error( "Debug loading failure" ) );
        } },
        { text: "RESET UPDATE FLAGS", action: () => {
          localStorage.removeItem( "update_soft_dismissed" );
          updateInfoText();
        } },
      ],
    },
    {
      title: "NOTIFICATIONS (fire in ~1s, leave app)",
      buttons: [
        { text: "TEST 1: LOGO",        action: () => { void NotificationManager.debugTest1LargeIconOnly(); } },
        { text: "TEST 2: BANNER",      action: () => { void NotificationManager.debugTest2BigPictureOnly(); } },
        { text: "TEST 3: LOGO+BANNER", action: () => { void NotificationManager.debugTest3DifferentImages(); } },
        { text: "TEST 4: HIDE LOGO",   action: () => { void NotificationManager.debugTest4HideLogo(); } },
        { text: "TEST 5: NO IMAGE",    action: () => { void NotificationManager.debugTest5NoImage(); } },
        { text: "SESSION PUSH NOW",    action: () => { void NotificationManager.debugTestSessionPushNow(); } },
        { text: "RESCHEDULE ALL",      action: () => { void NotificationManager.onForeground().then( refreshNotificationsDebugText ); } },
        { text: "CANCEL ALL",          action: () => { void NotificationManager.debugCancelAll().then( refreshNotificationsDebugText ); } },
        { text: "SHOW PENDING",        action: () => { void refreshNotificationsDebugText(); } },
      ],
    },
  ];

  // The console contains tools with very different purposes. Five concise
  // pages keep the touch targets useful without hiding any existing cheat.

  const page = ( ...titles: string[] ): SectionConfig[] =>
    titles.flatMap( title => sections.filter( section => section.title === title ) );

  const pages: DebugPage[] = [
    { title: "PLAY", sections: page( "SCREENS", "GAME STATE", "BLOCKS", "LOCALIZATION" ) },
    { title: "TUNE", sections: page( "TRIANGLE BENCH (ПУСТАЯ СЦЕНА)", "ЗАДНИЙ СВЕТ (RIM)", "ЯДРО (СИЛА / МАССА)", "MATERIAL INSPECTOR (РЕГУЛИРОВКА БЛОКОВ)" ) },
    { title: "TEST", sections: page( "TEXTURE TEST (ONE BLOCK PER LEVEL)", "TABLE LAYOUTS (2-4 TABLES, EVERY FORMATION)", "BARRIERS" ) },
    { title: "TOOLS", sections: page( "STRESS TEST (FPS)", "ENVIRONMENT", "RESULT OVERLAY", "OVERLAYS", "DIALOGS" ) },
    { title: "ACCOUNT", sections: page( "PERF DIAG", "BOOSTERS (GRANT TO SELF)", "OFFERS (TEST ONLY)", "NOTIFICATIONS (fire in ~1s, leave app)" ) },
  ];
  active_debug_page = Math.min( active_debug_page, pages.length - 1 );

  const tabs = document.createElement( "div" );
  tabs.style.display = "grid";
  tabs.style.gridTemplateColumns = "repeat(5, minmax(0, 1fr))";
  tabs.style.gap = "6px";
  tabs.style.marginBottom = "16px";
  overlay.appendChild( tabs );

  const page_content = document.createElement( "div" );
  overlay.appendChild( page_content );

  const renderPage = (): void =>
  {
    dynamic_buttons = [];
    tabs.replaceChildren();
    page_content.replaceChildren();
    for ( const [index, page] of pages.entries() )
    {
      const tab = document.createElement( "button" );
      tab.type = "button";
      tab.textContent = page.title;
      styleButton( tab, index === active_debug_page ? "#2e7d32" : "#385090" );
      tab.style.padding = "12px 4px";
      tab.addEventListener( "click", () =>
      {
        active_debug_page = index;
        renderPage();
        updateInfoText();
      } );
      tabs.appendChild( tab );
    }
    for ( const section of pages[active_debug_page].sections )
      page_content.appendChild( buildSection( section ) );
  };
  renderPage();

  info_el = document.createElement( "pre" );
  info_el.style.marginTop = "16px";
  info_el.style.padding = "10px";
  info_el.style.background = "rgba(255,255,255,0.06)";
  info_el.style.borderRadius = "8px";
  info_el.style.whiteSpace = "pre-wrap";
  info_el.style.font = "12px monospace";
  overlay.appendChild( info_el );

  return overlay;
}

function buildSection( section: SectionConfig ): HTMLDivElement
{
  const wrap = document.createElement( "div" );
  wrap.style.marginBottom = "18px";

  const title = document.createElement( "div" );
  title.textContent = section.title;
  title.style.font = "700 13px Arial, sans-serif";
  title.style.opacity = "0.7";
  title.style.marginBottom = "8px";
  title.style.letterSpacing = "1px";
  wrap.appendChild( title );

  const grid = document.createElement( "div" );
  grid.style.display = "grid";
  grid.style.gridTemplateColumns = "repeat(auto-fill, minmax(150px, 1fr))";
  grid.style.gap = "8px";

  for ( const cfg of section.buttons )
  {
    const btn = document.createElement( "button" );
    btn.type = "button";
    btn.textContent = cfg.getText ? cfg.getText() : ( cfg.text ?? "" );
    styleButton( btn, "#385090" );
    btn.addEventListener( "click", () =>
    {
      cfg.action();
      updateInfoText();
    } );
    grid.appendChild( btn );

    if ( cfg.getText )
      dynamic_buttons.push( { btn, cfg } );
  }

  wrap.appendChild( grid );
  return wrap;
}

function styleButton( btn: HTMLButtonElement, background: string ): void
{
  btn.style.padding = "10px 12px";
  btn.style.borderRadius = "8px";
  btn.style.border = "none";
  btn.style.background = background;
  btn.style.color = "#fff";
  btn.style.font = "700 12px Arial, sans-serif";
  btn.style.cursor = "pointer";
}

function updateInfoText(): void
{
  for ( const { btn, cfg } of dynamic_buttons )
    if ( cfg.getText ) btn.textContent = cfg.getText();

  if ( !info_el ) return;

  const screen = ScreenManager.getCurrentScreen() ?? "none";
  const stats = StatisticsStorage.load();

  const routing = getServerRoutingOverride();
  const routing_result = ServerRoutingService.getLastResult();
  const webapp: any = window.Telegram?.WebApp;
  info_el.textContent =
    `TG platform: ${webapp?.platform ?? "-"}\n` +
    `TG version: ${webapp?.version ?? "-"}\n` +
    `TG env: ${TelegramAPISettings.isTelegramEnvironment() ? "yes" : "no"}, TG links: ${TelegramAPISettings.canOpenTelegramLinks() ? "yes" : "no"}\n` +
    `Env: ${GlobalSettings.ENV}\n` +
    `Branch: ${GIT_BRANCH}\n` +
    `API: ${GlobalSettings.API_BASE_URL}${routing ? " (routed)" : ""}\n` +
    `Device: ${localStorage.getItem( "device_id" ) ?? "?"}\n` +
    `Routing: ${routing_result ? ( routing_result.error ?? ( routing_result.changed ? "changed" : "ok" ) ) : "not checked"}` +
    `${routing_result?.rule_id ? ` (rule ${routing_result.rule_id} → ${routing_result.server_key})` : ""}${routing_result?.transfer && routing_result.transfer !== "none" ? `, transfer ${routing_result.transfer}` : ""}\n` +
    `Screen: ${screen}\n` +
    `Level: ${stats.level}\n` +
    `Hearts: ${stats.hearts}/${stats.max_hearts}\n` +
    `No ads until: ${stats.no_ads_until > Date.now() ? `${Math.ceil( ( stats.no_ads_until - Date.now() ) / 60_000 )} min` : "-"}\n` +
    `Sounds: ${stats.sounds_enabled ? "ON" : "OFF"}\n` +
    `Music: ${stats.music_enabled ? "ON" : "OFF"}` +
    ( notifications_debug_text ? `\n\nNotifications:\n${notifications_debug_text}` : "" );
}

async function refreshNotificationsDebugText(): Promise<void>
{
  try
  {
    notifications_debug_text = await NotificationManager.debugDescribePending();
  }
  catch ( error )
  {
    notifications_debug_text = `error: ${String( error )}`;
  }
  updateInfoText();
}

function cycleLanguage(): void
{
  const langs = [ ...new Set( LocalizationManager.getAvailableLanguages() ) ];
  const current_index = langs.indexOf( LocalizationManager.getCurrentLanguage() );
  const next_lang = langs[ ( current_index + 1 ) % langs.length ];
  LocalizationManager.setLanguage( next_lang );
}

/**
 * Moves campaign progress. player_level is owned by the server and only ever
 * advances through level-complete, so writing it into local storage alone is
 * wiped by the next player sync — the round trip is what makes a cheat stick.
 */
async function setLevelOnServer( level: number ): Promise<void>
{
  if ( set_level_inflight )
    return;

  const target = Math.trunc( level );
  if ( !Number.isFinite( target ) || target < 1 )
    return;

  // Landing on a level the campaign does not have leaves the menu with nothing
  // to show and no way back except another cheat.
  const max_level = getMaxCampaignLevel();
  if ( max_level > 0 && target > max_level )
  {
    window.alert( `No such level — the campaign ends at ${ max_level }.` );
    return;
  }

  if ( target === StatisticsStorage.load().level )
    return;

  set_level_inflight = true;
  updateInfoText();

  try
  {
    const response = await debugSetLevel( target );
    adoptEconomyFromPlayer( response.player );
    // adoptEconomyFromPlayer carries the economy across but not these two.
    // last_menu_level is what the menu drives the cart from, and a debug jump
    // should land on the level rather than animate through everything in
    // between — REPLAY CART DRIVE is there when the animation is the point.
    StatisticsStorage.update( {
      seen_level: response.player.last_seen_level ?? target,
      last_menu_level: target,
    } );

    // The menu reads the level once, while it is being built, so without a
    // rebuild it keeps showing the old one — which is a large part of why the
    // old local-only cheat looked like it did nothing. Awaiting inside the
    // inflight guard also serialises repeated taps.
    if ( ScreenManager.getCurrentScreen() === Screens.MENU )
      await ScreenManager.showScreen( Screens.MENU );
  }
  catch ( error )
  {
    console.warn( "[DebugConsole] Failed to set level on server", error );
    window.alert( "Failed to set level on the server — see console for details." );
  }
  finally
  {
    set_level_inflight = false;
    updateInfoText();
  }
}

async function addHappyFallBall(): Promise<void>
{
  if ( add_happy_fall_ball_inflight ) return;
  add_happy_fall_ball_inflight = true;
  updateInfoText();
  try
  {
    const before = RewardWidgetsService.get()?.happy_fall?.balls_used ?? null;
    const state = await RewardWidgetsService.debugAddHappyFallBall();
    HappyFallScreen.active?.syncBalls( state );
    if ( before !== null && state.balls_used === before )
      window.alert( `Happy Fall balls are already full (${state.balls_total}/${state.balls_total})` );
  }
  catch ( error )
  {
    window.alert( `Add ball failed: ${error instanceof Error ? error.message : String( error )}` );
  }
  finally
  {
    add_happy_fall_ball_inflight = false;
    updateInfoText();
  }
}

function shiftLevelOnServer( delta: number ): Promise<void>
{
  return setLevelOnServer( StatisticsStorage.load().level + delta );
}

function setLevelPrompt(): Promise<void> | void
{
  const current = StatisticsStorage.load().level;
  const input = window.prompt( "Set progress to level:", String( current ) );
  if ( input === null )
    return;

  const level = Math.trunc( Number( input ) );
  if ( !Number.isFinite( level ) || level < 1 )
  {
    window.alert( "Enter a valid level number" );
    return;
  }

  return setLevelOnServer( level );
}

async function addHeartOnServer(): Promise<void>
{
  if ( add_heart_inflight )
    return;

  const stats = StatisticsStorage.load();
  if ( stats.hearts >= stats.max_hearts )
    return;

  add_heart_inflight = true;
  updateInfoText();

  try
  {
    const response = await rewardHeart();
    adoptEconomyFromPlayer( response.player );
  }
  catch ( error )
  {
    console.warn( "[DebugConsole] Failed to add heart on server", error );
  }
  finally
  {
    add_heart_inflight = false;
    updateInfoText();
  }
}

async function resetProgressOnServer(): Promise<void>
{
  if ( reset_progress_inflight )
    return;

  if ( !window.confirm( "Reset all game progress locally and on the server? This cannot be undone." ) )
    return;

  reset_progress_inflight = true;
  updateInfoText();

  try
  {
    const response = await resetProgress();
    adoptEconomyFromPlayer( response.player );

    // Keep the current account and environment. Everything else is recreated
    // from the freshly reset server profile on reload.
    const preserved_keys = [
      "player_auth_token",
      "player_id",
      "device_id",
      "runtime_environment_override",
      "server_routing_override",
      "api_base_override",
      "royal_smash_offer_entitlements",
    ];
    const preserved = preserved_keys
      .map( key => [ key, localStorage.getItem( key ) ] as const )
      .filter( ( entry ): entry is readonly [ string, string ] => entry[1] !== null );

    localStorage.clear();
    for ( const [ key, value ] of preserved )
      localStorage.setItem( key, value );

    window.location.reload();
  }
  catch ( error )
  {
    console.warn( "[DebugConsole] Failed to reset progress on server", error );
    reset_progress_inflight = false;
    updateInfoText();
  }
}

function toggleRuntimeEnvironment(): void
{
  const next_key = getNextRuntimeEnvironmentKey();
  const next_label = next_key === RuntimeEnvironmentKey.PROD ? 'PROD' : 'DEV';
  if ( !window.confirm( `Switch server environment to ${next_label} and reload?` ) ) return;

  setRuntimeEnvironmentOverride( next_key );
  window.location.reload();
}

async function checkServerRouting(): Promise<void>
{
  const result = await ServerRoutingService.check( 'manual' );
  console.info( `[DebugConsole] routing ${result.error ? `failed: ${result.error}` : `${result.changed ? "changed" : "unchanged"}: ${result.target ? `${result.target.api_url} (rule ${result.rule_id})` : "home server"}`}` );
  updateInfoText();
}

function clearServerRoutingOverride(): void
{
  if ( !getServerRoutingOverride() ) return;
  if ( !window.confirm( "Drop the server routing override and reload? The home server may route you again on the next check." ) ) return;
  setServerRoutingOverride( null );
  window.location.reload();
}

function createNewUserAndReload(): void
{
  if ( !window.confirm( "Create a new user and reload the app? This clears local progress and auth data." ) ) return;

  const env_override = localStorage.getItem( "runtime_environment_override" );
  const routing_override = localStorage.getItem( "server_routing_override" );
  localStorage.clear();
  if ( env_override )
    localStorage.setItem( "runtime_environment_override", env_override );
  if ( routing_override )
    localStorage.setItem( "server_routing_override", routing_override );

  resetDeviceIdForNewUser();
  window.location.reload();
}

function buildPasswordPrompt( onSubmit: ( password: string, setError: ( msg: string ) => void ) => Promise<boolean> ): HTMLDivElement
{
  const overlay = document.createElement( "div" );
  overlay.id = "debugPasswordPrompt";
  overlay.style.position = "fixed";
  overlay.style.left = "0";
  overlay.style.top = "0";
  overlay.style.right = "0";
  overlay.style.bottom = "0";
  overlay.style.zIndex = "2147483647";
  overlay.style.background = "rgba(10, 12, 20, 0.94)";
  overlay.style.display = "flex";
  overlay.style.alignItems = "center";
  overlay.style.justifyContent = "center";
  overlay.style.font = "13px Arial, sans-serif";

  const box = document.createElement( "div" );
  box.style.width = "260px";
  box.style.padding = "18px";
  box.style.borderRadius = "10px";
  box.style.background = "rgba(255,255,255,0.06)";
  box.style.border = "1px solid rgba(255,255,255,0.15)";

  const title = document.createElement( "div" );
  title.textContent = "DEBUG ACCESS";
  title.style.color = "#fff";
  title.style.font = "700 14px Arial, sans-serif";
  title.style.marginBottom = "12px";

  const input = document.createElement( "input" );
  input.type = "password";
  input.placeholder = "Password";
  input.style.width = "100%";
  input.style.boxSizing = "border-box";
  input.style.padding = "10px";
  input.style.borderRadius = "8px";
  input.style.border = "1px solid rgba(255,255,255,0.2)";
  input.style.background = "rgba(255,255,255,0.08)";
  input.style.color = "#fff";
  input.style.font = "14px monospace";
  input.style.marginBottom = "10px";
  input.style.outline = "none";

  const error_el = document.createElement( "div" );
  error_el.style.color = "#f55";
  error_el.style.font = "11px Arial, sans-serif";
  error_el.style.marginBottom = "10px";
  error_el.style.minHeight = "14px";

  const row = document.createElement( "div" );
  row.style.display = "flex";
  row.style.gap = "8px";

  const submit_btn = document.createElement( "button" );
  submit_btn.type = "button";
  submit_btn.textContent = "UNLOCK";
  styleButton( submit_btn, "#385090" );
  submit_btn.style.flex = "1";

  const cancel_btn = document.createElement( "button" );
  cancel_btn.type = "button";
  cancel_btn.textContent = "CANCEL";
  styleButton( cancel_btn, "#555" );
  cancel_btn.addEventListener( "click", () => overlay.remove() );

  const submit = () =>
  {
    error_el.textContent = "";
    submit_btn.disabled = true;
    submit_btn.textContent = "…";
    void onSubmit( input.value, ( msg ) => { error_el.textContent = msg; } )
      .then( ( success ) => { if ( success ) overlay.remove(); } )
      .finally( () =>
      {
        submit_btn.disabled = false;
        submit_btn.textContent = "UNLOCK";
      } );
  };

  submit_btn.addEventListener( "click", submit );
  input.addEventListener( "keydown", ( ev ) => { if ( ev.key === "Enter" ) submit(); } );

  row.appendChild( submit_btn );
  row.appendChild( cancel_btn );

  box.appendChild( title );
  box.appendChild( input );
  box.appendChild( error_el );
  box.appendChild( row );
  overlay.appendChild( box );

  requestAnimationFrame( () => input.focus() );

  return overlay;
}

export const DebugConsoleManager = {
  open(): void
  {
    NetworkManager.setDebugActive( true );
    this.attachEntryButton();

    if ( !panel_el )
    {
      panel_el = buildPanel();
      document.body.appendChild( panel_el );
    }

    updateInfoText();
    panel_el.style.display = "block";
  },

  promptPassword(): void
  {
    if ( document.getElementById( "debugPasswordPrompt" ) ) return;

    const overlay = buildPasswordPrompt( async ( password, setError ) =>
    {
      if ( password === "" )
      {
        setError( "Enter password" );
        return false;
      }

      let response: Response;
      try
      {
        response = await fetch( `${GlobalSettings.SUPPORT_BASE_URL}/auth/verify-password`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify( { password } ),
        } );
      }
      catch
      {
        setError( "Network error, try again" );
        return false;
      }

      if ( response.ok )
      {
        DebugConsoleManager.open();
        return true;
      }

      if ( response.status === 429 )
      {
        setError( "Too many attempts" );
        return false;
      }

      const data = await response.json().catch( () => ( {} ) ) as { attempts_left?: number };
      setError( data.attempts_left !== undefined
        ? `Wrong password, ${data.attempts_left} attempts left`
        : "Wrong password" );
      return false;
    } );

    document.body.appendChild( overlay );
    ( overlay as any )._input?.focus();
  },

  close(): void
  {
    NetworkManager.setDebugActive( false );
    if ( panel_el )
      panel_el.style.display = "none";
  },

  attachEntryButton(): void
  {
    if ( entry_attached ) return;
    entry_attached = true;

    const button = document.createElement( "button" );
    button.type = "button";
    button.textContent = "DEBUG";
    button.style.position = "fixed";
    button.style.left = "12px";
    button.style.bottom = "100px";
    button.style.zIndex = "2147483647";
    button.style.padding = "10px 14px";
    button.style.borderRadius = "10px";
    button.style.border = "2px solid #000";
    button.style.background = "#ffd200";
    button.style.color = "#000";
    button.style.font = "700 12px Arial, sans-serif";
    button.style.boxShadow = "0 2px 6px rgba(0,0,0,0.35)";
    button.style.cursor = "pointer";

    button.addEventListener( "click", () => DebugConsoleManager.open() );

    document.body.appendChild( button );
  },

  attachSecretCodeListener(): void
  {
    if ( tap_code_attached ) return;
    tap_code_attached = true;

    if ( 'PointerEvent' in window )
    {
      document.addEventListener( "pointerdown", ( ev: PointerEvent ) =>
      {
        if ( ev.isPrimary === false ) return;
        registerTapCodeStep( getTapQuadrant( ev.clientX, ev.clientY ) );
      }, { passive: true } );
      return;
    }

    document.addEventListener( "touchstart", ( ev: TouchEvent ) =>
    {
      const touch = ev.changedTouches?.[ 0 ];
      if ( !touch ) return;
      registerTapCodeStep( getTapQuadrant( touch.clientX, touch.clientY ) );
    }, { passive: true } );
  }
};
